import * as React from 'react'
import { UseQueryResult } from 'react-query'
import { Organization, Webhook } from '@yuno/types'
import {
  useGetWebhooksV2,
  useDeleteWebhookV2,
  queryClient,
  useUpdateWebhookV2,
} from '@yuno/dashboard-api-mfe'
import { useAccount } from '@yuno/dashboard-container-mfe'
import {
  actionsNames,
  DeleteWebhookProps,
  EWebhookStatus,
  TWebhook,
} from '../../typings/pages'
import { storage, Item } from '@yuno/dashboard-container-mfe'

export const useWebhooks = () => {
  const account = useAccount()
  const {
    data,
    isLoading,
    refetch,
    isFetching,
  }: UseQueryResult<Webhook.WebHook[], unknown> = useGetWebhooksV2({
    accountCode: account?.code,
  })

  React.useEffect(() => {
    if (account?.code) {
      refetch()
    }
  }, [account?.code])

  const webhooks = React.useMemo(() => {
    if (data?.length > 0) {
      return data.map((webhook: TWebhook) => {
        return {
          ...webhook,
        }
      })
    }

    return []
  }, [data])

  return {
    isLoading: isLoading || isFetching,
    webhooks,
  }
}

export const useWebhookDelete = (props: DeleteWebhookProps) => {
  const { onSuccess } = props

  const onSuccessCallback = () => {
    queryClient.refetchQueries(['webhooks'])
    onSuccess()
  }

  const { mutate, isLoading, status } = useDeleteWebhookV2({
    onSuccessCallback,
  })

  const { code: accountCode }: Organization.Account = storage.getItem(
    Item.ACCOUNT,
  )

  const handleDelete = (code: string) => {
    mutate({ code, accountCode })
  }

  return {
    isLoading,
    handleDelete,
    statusDelete: status,
  }
}

export const useWebhookHelpers = () => {
  const transformStatus = (status: string) => {
    let switchStatus = false
    switchStatus = status === EWebhookStatus.active
    return switchStatus
  }

  const itemsNoTooltip = 4

  const getTotalActions = (webhook) => {
    const actions = []
    actionsNames.forEach((actionName) => {
      if (webhook[actionName]) {
        const actionsType = webhook[actionName].map((trigger) => {
          const action = actionName.split('_')[0]
          return `${`${action.charAt(0).toUpperCase()}${action.slice(
            1,
          )}`} ${trigger.name.toLowerCase()}`
        })
        actions.push(...actionsType)
      }
    })

    const moreThanThree = actions.length > 3
    const tooltipLabel = actions
      .slice(3, actions.length)
      .map(
        (item, index) =>
          `${index > 0 ? ',' : ''} ${item}${
            index === actions.length - itemsNoTooltip ? '.' : ''
          }`,
      )

    return {
      actions: moreThanThree ? actions.slice(0, 3) : actions,
      actionsTooltip: moreThanThree ? actions.slice(3, actions.length) : [],
      tooltipLabel,
    }
  }

  return {
    helpers: {
      transformStatus,
      getTotalActions,
    },
  }
}

export const useEditWebhookState = () => {
  const onSuccessCallback = () => {
    queryClient.invalidateQueries(['webhooks'])
  }

  const { mutate, status, isLoading } = useUpdateWebhookV2({
    onSuccessCallback,
  })

  const { code: accountCode }: Organization.Account = storage.getItem(
    Item.ACCOUNT,
  )

  const handleChangeState = (code: number, state: string) => {
    const payload = {
      state: state,
    }

    mutate({ code, payload, accountCode })
  }

  return {
    status,
    isLoading,
    handleChangeState,
  }
}
